/**
 * Per-user "Acting As" context.
 *
 * Without a `<TargetUidProvider>` higher up the tree, every settings hook
 * targets the logged-in user's own uid (the unchanged self-edit flow).
 * When a `<TargetUidProvider uid="…">` wraps the tree (admin route), the
 * same hooks transparently switch over to admin-target callables that
 * read/write the supplied uid instead.
 *
 * The admin's own auth identity never changes — we never impersonate.
 */
import * as React from 'react';
import { useUser } from './AuthProvider';

interface TargetCtx {
  /** The uid the surrounding <TargetUidProvider> targets, or null. */
  targetUid: string | null;
  /** True if a <TargetUidProvider> is mounted (admin-mode). */
  isAdminMode: boolean;
}

const Ctx = React.createContext<TargetCtx>({ targetUid: null, isAdminMode: false });

export function TargetUidProvider({
  uid,
  children,
}: {
  uid: string;
  children: React.ReactNode;
}) {
  const value = React.useMemo<TargetCtx>(
    () => ({ targetUid: uid, isAdminMode: true }),
    [uid],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The uid currently being targeted by an <TargetUidProvider>, or null. */
export function useTargetUid(): string | null {
  return React.useContext(Ctx).targetUid;
}

/** True iff a <TargetUidProvider> is mounted above this component. */
export function useIsAdminMode(): boolean {
  return React.useContext(Ctx).isAdminMode;
}

/**
 * The uid that data hooks should read/write against:
 *   - In admin-mode: the explicitly supplied targetUid.
 *   - Otherwise: the logged-in user's own uid (self-edit).
 *
 * Returns `null` when no user is logged in and no target is set.
 */
export function useEffectiveUid(): string | null {
  const target = useTargetUid();
  const { user } = useUser();
  return target ?? user?.uid ?? null;
}
